import { formatDateTime } from '@pr-app/shared';
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist';
// Vite's `?url` suffix resolves this to the built worker script's own
// same-origin URL rather than inlining it — pdf.js loads it via
// `new Worker(workerSrc, { type: 'module' })`, so it must stay a real,
// separately-fetchable asset. oxlint's `import/default` check resolves the
// underlying `pdf.worker.min.mjs` file itself (which genuinely has no
// default export — it's a worker entry point) and cannot see that Vite's
// `?url` query rewrites the import to a plain string at build time; this is
// a real gap in the linter's understanding of a bundler-specific import
// form, not a mistake in this import.
// eslint-disable-next-line import/default -- Vite `?url` import; see comment above
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { fetchAttachmentBlob, type Attachment } from '../api/attachments';
import type { TeamsThemeName } from '../teams/TeamsProvider';

// Module-load side effect, same as any other library-wide singleton
// configuration — pdf.js requires this be set before the first
// `getDocument()` call, and every caller in this app shares one worker.
GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

const DOCX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const PDF_CONTENT_TYPE = 'application/pdf';

function isImageContentType(contentType: string): boolean {
  return contentType.startsWith('image/');
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * Literal colors for each Teams theme, confirmed against this repo's
 * installed `@fluentui/tokens` (`colorNeutralBackground1`,
 * `colorNeutralForeground1`, `colorNeutralStroke1`). This HTML renders inside
 * a sandboxed `srcDoc` iframe — a separate document — so Fluent's CSS custom
 * properties/tokens from the parent page cannot cross that boundary; these
 * must stay literal hex values, not token references (#266).
 */
const DOCUMENT_PREVIEW_PALETTE: Record<
  TeamsThemeName,
  { background: string; foreground: string; border: string }
> = {
  default: { background: '#ffffff', foreground: '#242424', border: '#d1d1d1' },
  dark: { background: '#292929', foreground: '#ffffff', border: '#666666' },
  contrast: { background: '#000000', foreground: '#ffffff', border: '#ffffff' },
};

/**
 * Wraps a rendered document fragment in a minimal, self-contained HTML page —
 * no external CSS, fonts, or network references — so it is safe to open as a
 * `Blob` URL with no further requests. Colored to match the app's active
 * Teams theme (`themeName`) since this page cannot inherit Fluent tokens
 * across the sandboxed iframe boundary — see `DOCUMENT_PREVIEW_PALETTE`.
 */
function wrapAsHtmlDocument(title: string, bodyHtml: string, themeName: TeamsThemeName): string {
  const palette = DOCUMENT_PREVIEW_PALETTE[themeName];
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    margin: 0;
    padding: 32px;
    max-width: 900px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
    background-color: ${palette.background};
    color: ${palette.foreground};
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 24px;
  }
  th, td {
    border: 1px solid ${palette.border};
    padding: 6px 10px;
    text-align: left;
  }
  h2 {
    margin-top: 32px;
  }
  img {
    max-width: 100%;
  }
</style>
</head>
<body>
${bodyHtml}
</body>
</html>`;
}

/** Renders a `.docx` attachment's already-fetched bytes to a self-contained HTML page. */
async function renderDocxToHtml(
  fileName: string,
  arrayBuffer: ArrayBuffer,
  themeName: TeamsThemeName,
): Promise<string> {
  const { default: mammoth } = await import('mammoth');
  const { value: bodyHtml } = await mammoth.convertToHtml({ arrayBuffer });
  return wrapAsHtmlDocument(fileName, bodyHtml, themeName);
}

/**
 * Loads an already-fetched PDF's bytes via pdf.js, without deciding how it is
 * displayed — `AttachmentPreviewDialog` owns the `<canvas>` rendering, the
 * same split this file already keeps for `html` (rendered markup, not DOM).
 *
 * #271: this replaces the old `URL.createObjectURL` + `<iframe src={url}>`
 * approach. That was a navigation into a NESTED browsing context, and Teams
 * always embeds this app inside its own sandboxed iframe — sandbox flags
 * inherit to every descendant browsing context with no way to un-set them,
 * and Chrome's built-in PDF viewer is exactly the kind of internal
 * plugin-like content sandbox inheritance blocks. Rendering to `<canvas>` in
 * this document, with no nested browsing context at all, sidesteps that
 * mechanism by construction — the same reason `html` (an inline `srcDoc`,
 * not a blob navigation) was never affected.
 *
 * Also returns the loading task's own `destroy`: `PDFDocumentProxy` itself
 * has no `destroy()` (checked against the installed `pdfjs-dist` package,
 * not assumed) — only `PDFDocumentLoadingTask`, what `getDocument()` returns
 * before its `.promise` resolves, can release the document's worker-side
 * resources.
 */
async function loadPdfDocument(
  data: ArrayBuffer,
): Promise<{ pdfDocument: PDFDocumentProxy; destroy: () => void }> {
  const loadingTask = getDocument({ data });
  const pdfDocument = await loadingTask.promise;
  return { pdfDocument, destroy: () => void loadingTask.destroy() };
}

/** Renders every worksheet of an already-fetched `.xlsx` attachment to a self-contained HTML page. */
async function renderXlsxToHtml(
  fileName: string,
  arrayBuffer: ArrayBuffer,
  themeName: TeamsThemeName,
): Promise<string> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  const multipleSheets = workbook.worksheets.length > 1;
  const sheetsHtml = workbook.worksheets
    .map((sheet) => {
      const heading = multipleSheets ? `<h2>${escapeHtml(sheet.name)}</h2>` : '';
      const rows: string[] = [];
      sheet.eachRow((row) => {
        // `row.values` is normally an array (1-indexed — index 0 is always
        // empty), but exceljs's own type also allows a sparse `{ [key]:
        // CellValue }` object form; treat that shape as an empty row rather
        // than assuming the array branch.
        const values = row.values;
        const cells = (Array.isArray(values) ? values.slice(1) : [])
          .map((cellValue) => `<td>${escapeHtml(cellValueToText(cellValue))}</td>`)
          .join('');
        rows.push(`<tr>${cells}</tr>`);
      });
      return `${heading}<table>${rows.join('')}</table>`;
    })
    .join('');

  return wrapAsHtmlDocument(fileName, sheetsHtml, themeName);
}

/** Stringifies a primitive value; anything else (an object with no readable
 *  shape) becomes an empty string rather than risking `[object Object]`. */
function primitiveToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return formatDateTime(value);
  return '';
}

function cellValueToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return formatDateTime(value);
  if (typeof value === 'object') {
    // Rich text / formula result objects — best-effort readable text.
    const maybeText = (value as { text?: unknown; result?: unknown }).text;
    if (typeof maybeText === 'string') return maybeText;
    const maybeResult = (value as { result?: unknown }).result;
    return primitiveToText(maybeResult);
  }
  return primitiveToText(value);
}

/**
 * What `getAttachmentPreview` resolved to, and how `AttachmentPreviewDialog`
 * should render it:
 *  - `image` carries an already-created object URL (blob or, for a
 *    freshly-uploaded file with no round trip yet, the local `File` itself) —
 *    the caller is responsible for revoking it once done.
 *  - `pdf` carries a loaded pdf.js document (`pdfDocument`) plus its own
 *    `destroy()` — the caller renders pages to `<canvas>` and calls `destroy()`
 *    once done. No object URL, no iframe navigation (#271).
 *  - `html` is rendered docx/xlsx content, safe to drop into a sandboxed
 *    `srcdoc` iframe with no further requests.
 *  - `unsupported` covers a content type with no in-app renderer (legacy
 *    `.doc`/`.xls`, anything unrecognized) AND a docx/xlsx conversion that
 *    failed after the blob was already fetched — either way, the raw blob is
 *    still there so a Download action keeps working.
 */
export type PreviewResult =
  | { kind: 'image'; url: string }
  | { kind: 'pdf'; pdfDocument: PDFDocumentProxy; destroy: () => void }
  | { kind: 'html'; html: string }
  | { kind: 'unsupported'; blob: Blob; fileName: string };

/**
 * Resolves an attachment to previewable content, without deciding how it is
 * displayed — that split is what lets every call site (`ReviewerQueue`,
 * `AttachmentsSection`, `ReviewSummary`) share one `AttachmentPreviewDialog`
 * instead of each opening its own new browser tab or hand-rolled lightbox.
 *
 * `localFile`, when given, is the in-memory `File` from an upload still in
 * this session — used directly for an image (`URL.createObjectURL`, no
 * network round trip) or a PDF (its bytes loaded straight into pdf.js via
 * `arrayBuffer()`, same no-round-trip reasoning). This is deliberate: it is
 * the actual fix for the "blank image on Review & Submit" bug (#260), not a
 * separate optimisation — using the freshly-uploaded file directly is exactly
 * what avoids the authenticated re-fetch that could previously fail silently
 * and forever. #268 widened it from images to PDFs too, and #271 changed
 * PDF's own mechanism (object URL + iframe → pdf.js + canvas) without
 * touching that reasoning. docx/xlsx and legacy doc/xls are deliberately
 * excluded — they need actual parsing (`renderDocxToHtml`/`renderXlsxToHtml`)
 * or have no in-app renderer at all, so they always go through the fetch
 * path below.
 *
 * A fetch that fails outright (no blob at all) throws — the dialog shows a
 * retry state for that. Everything else — an unsupported content type, or a
 * docx/xlsx conversion failure on an otherwise-successful fetch — resolves to
 * `'unsupported'` rather than throwing, so a Download action is always
 * available once bytes exist.
 */
export async function getAttachmentPreview(
  attachment: Attachment,
  localFile?: File,
  themeName: TeamsThemeName = 'default',
): Promise<PreviewResult> {
  if (localFile && isImageContentType(attachment.contentType)) {
    return { kind: 'image', url: URL.createObjectURL(localFile) };
  }
  if (localFile && attachment.contentType === PDF_CONTENT_TYPE) {
    const { pdfDocument, destroy } = await loadPdfDocument(await localFile.arrayBuffer());
    return { kind: 'pdf', pdfDocument, destroy };
  }

  const blob = await fetchAttachmentBlob(attachment.id);

  if (isImageContentType(attachment.contentType)) {
    return { kind: 'image', url: URL.createObjectURL(blob) };
  }

  if (attachment.contentType === PDF_CONTENT_TYPE) {
    const { pdfDocument, destroy } = await loadPdfDocument(await blob.arrayBuffer());
    return { kind: 'pdf', pdfDocument, destroy };
  }

  if (
    attachment.contentType === DOCX_CONTENT_TYPE ||
    attachment.contentType === XLSX_CONTENT_TYPE
  ) {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const html =
        attachment.contentType === DOCX_CONTENT_TYPE
          ? await renderDocxToHtml(attachment.fileName, arrayBuffer, themeName)
          : await renderXlsxToHtml(attachment.fileName, arrayBuffer, themeName);
      return { kind: 'html', html };
    } catch {
      // A parse failure must never leave the user with nothing — the blob
      // was fetched successfully, so a Download action still works.
      return { kind: 'unsupported', blob, fileName: attachment.fileName };
    }
  }

  return { kind: 'unsupported', blob, fileName: attachment.fileName };
}
